import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { expensesAPI, lotesAPI, authAPI } from '../lib/api';
import { formatCurrency, formatDate, CATEGORIAS, getPhaseLabel, getCategoryColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CurrencyDollar, Plus, Trash, Funnel, Warning, ShieldCheck, PencilSimple } from '@phosphor-icons/react';

export default function ExpensesList() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    lote_id: 'all',
    categoria: 'all',
  });
  
  // Modal eliminar con super usuario
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [deleteCode, setDeleteCode] = useState('');
  const [deletingExpense, setDeletingExpense] = useState(false);
  
  // Modal editar con super usuario
  const [showEditModal, setShowEditModal] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [editCode, setEditCode] = useState('');
  const [validatingEdit, setValidatingEdit] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.lote_id, filters.categoria]);

  const loadData = async () => {
    try {
      const lotesRes = await lotesAPI.getAll();
      setLotes(lotesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.lote_id && filters.lote_id !== 'all') params.lote_id = filters.lote_id;
      if (filters.categoria && filters.categoria !== 'all') params.categoria = filters.categoria;
      
      const response = await expensesAPI.getAll(params);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (expense) => {
    setExpenseToDelete(expense);
    setDeleteCode('');
    setShowDeleteModal(true);
  };

  const openEditModal = (expense) => {
    setExpenseToEdit(expense);
    setEditCode('');
    setShowEditModal(true);
  };

  const handleValidateEdit = async () => {
    if (!editCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    setValidatingEdit(true);
    try {
      await authAPI.validateSuperuser(editCode);
      setShowEditModal(false);
      // Navegar al formulario de edición con el código ya validado
      navigate(`/gastos/editar/${expenseToEdit.id}?code=${encodeURIComponent(editCode)}`);
    } catch (error) {
      toast.error('Código de super usuario inválido');
    } finally {
      setValidatingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCode || !expenseToDelete) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setDeletingExpense(true);
    try {
      // Validar código
      await authAPI.validateSuperuser(deleteCode);
      
      // Eliminar
      await expensesAPI.delete(expenseToDelete.id, deleteCode);
      toast.success('Gasto eliminado');
      setShowDeleteModal(false);
      loadExpenses();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error('Error al eliminar');
      }
    } finally {
      setDeletingExpense(false);
    }
  };

  // Calcular total en DOP (convirtiendo USD si es necesario)
  const totalExpenses = expenses.reduce((sum, e) => {
    // Usar total_dop si existe, sino convertir
    if (e.total_dop !== undefined) {
      return sum + e.total_dop;
    }
    // Fallback: si no hay total_dop, usar total y asumir DOP
    return sum + e.total;
  }, 0);

  const getLoteName = (loteId) => {
    return lotes.find((l) => l.id === loteId)?.nombre || 'Desconocido';
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="expenses-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Gastos
            </h1>
            <p className="text-muted-foreground">
              Gestiona todos los gastos registrados
            </p>
          </div>
          <Link to="/gastos/nuevo">
            <Button className="bg-[#E73425] hover:bg-[#D81C23] btn-animate" data-testid="new-expense-btn">
              <Plus size={18} className="mr-2" />
              Nuevo Gasto
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="border-border/60">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Funnel size={18} />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              
              <Select
                key={`lote-filter-${lotes.length}`}
                value={filters.lote_id}
                onValueChange={(value) => {
                  console.log('Lote filter changed to:', value);
                  setFilters((prev) => ({ ...prev, lote_id: value }));
                }}
              >
                <SelectTrigger className="w-[200px]" data-testid="filter-lote">
                  <SelectValue placeholder="Todos los lotes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los lotes</SelectItem>
                  {lotes.map((lote) => (
                    <SelectItem key={lote.id} value={lote.id}>
                      {lote.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.categoria}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, categoria: value }))}
              >
                <SelectTrigger className="w-[200px]" data-testid="filter-categoria">
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIAS.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(filters.lote_id !== 'all' || filters.categoria !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({ lote_id: 'all', categoria: 'all' })}
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Total Gastos</p>
              <p className="metric-value text-[#E73425]">{expenses.length}</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Monto Total (RD$)</p>
              <p className="metric-value text-[#F69031]">RD$ {totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground mt-1">Incluye gastos en USD convertidos</p>
            </CardContent>
          </Card>
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Promedio (RD$)</p>
              <p className="metric-value text-slate-600">
                {expenses.length > 0 ? `RD$ ${(totalExpenses / expenses.length).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'RD$ 0.00'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Expenses Table */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : expenses.length > 0 ? (
          <Card className="border-border/60">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b table-header">
                      <th className="text-left p-4">Fecha</th>
                      <th className="text-left p-4">Lote</th>
                      <th className="text-left p-4">Fase</th>
                      <th className="text-left p-4">Categoría</th>
                      <th className="text-left p-4">Proveedor</th>
                      <th className="text-left p-4">Descripción</th>
                      <th className="text-right p-4">Total</th>
                      <th className="text-center p-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((exp) => (
                      <tr key={exp.id} className="border-b table-row" data-testid={`expense-${exp.id}`}>
                        <td className="p-4">{formatDate(exp.fecha)}</td>
                        <td className="p-4">
                          <Link
                            to={`/lotes/${exp.lote_id}`}
                            className="text-primary hover:underline font-medium"
                          >
                            {getLoteName(exp.lote_id)}
                          </Link>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className={`phase-badge phase-${exp.fase.replace('.', '-')}`}>
                            {getPhaseLabel(exp.fase)}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: getCategoryColor(exp.categoria) }}
                            />
                            {CATEGORIAS.find((c) => c.value === exp.categoria)?.label || exp.categoria}
                            {exp.subcategoria && (
                              <span className="text-xs text-muted-foreground">
                                ({exp.subcategoria})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 max-w-[150px] truncate" title={exp.proveedor_nombre || exp.proveedor || ''}>
                          {exp.proveedor_nombre || exp.proveedor || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="p-4 max-w-[200px] truncate" title={exp.descripcion}>
                          {exp.descripcion}
                        </td>
                        <td className="p-4 text-right font-bold font-mono">
                          {exp.moneda === 'USD' ? (
                            <div className="flex flex-col items-end">
                              <span className="text-green-600">US$ {exp.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              <span className="text-xs text-muted-foreground">(RD$ {exp.total_dop?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'})</span>
                            </div>
                          ) : (
                            <span>RD$ {exp.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:text-blue-700"
                              onClick={() => openEditModal(exp)}
                              data-testid={`edit-expense-${exp.id}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => openDeleteModal(exp)}
                              data-testid={`delete-expense-${exp.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="empty-state">
                <CurrencyDollar size={64} className="text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No hay gastos registrados</h3>
                <p className="text-muted-foreground mb-6">
                  {filters.lote_id !== 'all' || filters.categoria !== 'all'
                    ? 'No se encontraron gastos con los filtros seleccionados'
                    : 'Comience registrando su primer gasto'}
                </p>
                <Link to="/gastos/nuevo">
                  <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                    <Plus size={18} className="mr-2" />
                    Nuevo Gasto
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Modal de Eliminar Gasto (con Super Usuario) */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Gasto
              </DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer
              </DialogDescription>
            </DialogHeader>
            
            {expenseToDelete && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 mb-2">
                    Está a punto de eliminar el gasto:
                  </p>
                  <p className="font-semibold text-red-700">{expenseToDelete.descripcion}</p>
                  <p className="text-sm text-red-600 mt-1">
                    Monto: {formatCurrency(expenseToDelete.total)}
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-expense-code">Código de Super Usuario</Label>
                  <Input
                    id="delete-expense-code"
                    type="password"
                    value={deleteCode}
                    onChange={(e) => setDeleteCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="delete-expense-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deletingExpense || !deleteCode}
              >
                {deletingExpense ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Gasto
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal de Editar Gasto (con Super Usuario) */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <PencilSimple size={24} />
                Editar Gasto
              </DialogTitle>
              <DialogDescription>
                Ingrese el código de super usuario para editar
              </DialogDescription>
            </DialogHeader>
            
            {expenseToEdit && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 mb-2">
                    Editar el gasto:
                  </p>
                  <p className="font-semibold text-blue-700">{expenseToEdit.descripcion}</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Monto: {formatCurrency(expenseToEdit.total)}
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-expense-code">Código de Super Usuario</Label>
                  <Input
                    id="edit-expense-code"
                    type="password"
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="edit-expense-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleValidateEdit}
                disabled={validatingEdit || !editCode}
              >
                {validatingEdit ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <ShieldCheck size={16} className="mr-2" />
                    Validar y Editar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
